import { createContext, useContext } from 'react';
import type { ProfileNavEntry } from './ProfileNav';

/**
 * The name of the panel currently open, for `<ProfileSection>` to head its content with.
 *
 * **It is DERIVED from the nav, never retyped.** The rail and the heading are one fact, and a
 * panel that spelled its own name would be a second place for it to live — renaming a section
 * in the nav would leave the heading stating the old word, on the one screen that exists to
 * say which section you are reading.
 *
 * A context rather than a prop threaded through every panel, for the reason
 * `ProfileCardSurfaceContext` is one: this is a fact about the SURFACE, and 22 call sites each
 * remembering to pass it is 22 chances for one of them to forget. A future panel gets the
 * heading by existing.
 */
export const ProfileSectionTitleContext = createContext<string | undefined>(undefined);

export function useProfileSectionTitle() {
  return useContext(ProfileSectionTitleContext);
}

/**
 * The label of the entry a key names, looked up across groups and top-level entries.
 *
 * Returns undefined for a key the nav does not carry — a panel rendered for a key with no
 * entry gets no heading rather than an invented one, which is the honest answer and is also
 * what the old behaviour was.
 */
export function findNavLabel(
  entries: ProfileNavEntry[],
  key: string | undefined,
): string | undefined {
  if (!key) return undefined;

  for (const entry of entries) {
    if ('items' in entry) {
      const hit = entry.items.find((item) => item.key === key);
      if (hit) return hit.label;
      continue;
    }

    if (entry.key === key) return entry.label;
  }

  return undefined;
}
